import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Clock, Loader2, Pencil, Check, X } from "lucide-react";
import { useOvertimeBalance, useSetCarryOver } from "@/hooks/useClockify";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/lib/date-utils";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface OvertimeSummaryCardProps {
  personId: string;
  year: number;
}

export function OvertimeSummaryCard({
  personId,
  year,
}: OvertimeSummaryCardProps) {
  const { data, isLoading } = useOvertimeBalance(personId, year);
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const setCarryOver = useSetCarryOver();

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [showDetails, setShowDetails] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const formatHours = (h: number) => {
    const sign = h >= 0 ? "+" : "";
    return `${sign}${h.toFixed(1)}h`;
  };

  const handleEditStart = () => {
    setEditValue(String(data.carry_over));
    setEditing(true);
  };

  const handleSave = () => {
    const hours = parseFloat(editValue);
    if (isNaN(hours)) return;
    // Carry-over displayed for year Y is stored as overtime_carry_over.year = Y-1
    setCarryOver.mutate(
      { personId, year: year - 1, hours },
      { onSuccess: () => setEditing(false) },
    );
  };

  // Filter out future months for the current year
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based
  const visibleMonths =
    year === currentYear
      ? data.months.filter((m) => m.month <= currentMonth)
      : data.months;

  // Compute running balance per month: carry_over + cumulative deltas
  let runningBalance = data.carry_over;
  const monthBalances = visibleMonths.map((m) => {
    runningBalance += m.delta;
    return Math.round(runningBalance * 100) / 100;
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Overtime {year}
        </CardTitle>
        <CardDescription className="text-xs">
          As of {formatDate(data.computed_until)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold">{formatHours(data.balance)}</p>
            <p className="text-xs text-muted-foreground">Balance</p>
          </div>
          <div>
            {editing ? (
              <div className="flex items-center gap-1 justify-center">
                <Input
                  type="number"
                  step="0.5"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-20 h-7 text-sm text-center"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") setEditing(false);
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleSave}
                  disabled={setCarryOver.isPending}
                >
                  {setCarryOver.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setEditing(false)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div
                className={isAdmin ? "cursor-pointer group" : ""}
                onClick={isAdmin ? handleEditStart : undefined}
              >
                <p className="text-lg font-medium inline-flex items-center gap-1">
                  {formatHours(data.carry_over)}
                  {isAdmin && (
                    <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                  )}
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Carry-over</p>
          </div>
          <div>
            <p className="text-lg font-medium">-{data.paid_out.toFixed(1)}h</p>
            <p className="text-xs text-muted-foreground">Paid out</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="show-details"
            checked={showDetails}
            onCheckedChange={(checked) => setShowDetails(checked === true)}
          />
          <Label
            htmlFor="show-details"
            className="text-xs text-muted-foreground cursor-pointer"
          >
            Show details
          </Label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="text-left py-1 pr-2">Month</th>
                <th className="text-right py-1 px-1">Work</th>
                {showDetails && <th className="text-right py-1 px-1">PH</th>}
                {showDetails && <th className="text-right py-1 px-1">Abs</th>}
                {showDetails && <th className="text-right py-1 px-1">%</th>}
                <th className="text-right py-1 px-1">Expected</th>
                <th className="text-right py-1 px-1">Actual</th>
                <th className="text-right py-1 px-1">Delta</th>
                <th className="text-right py-1 pl-1">Balance</th>
              </tr>
            </thead>
            <tbody>
              {visibleMonths.map((m, i) => (
                <tr key={m.month} className="border-b border-muted/30">
                  <td className="py-1 pr-2 font-medium">
                    {MONTH_NAMES[m.month - 1]}
                  </td>
                  <td className="text-right py-1 px-1">{m.work_days}</td>
                  {showDetails && (
                    <td className="text-right py-1 px-1">
                      {m.public_holidays}
                    </td>
                  )}
                  {showDetails && (
                    <td className="text-right py-1 px-1">{m.absences_taken}</td>
                  )}
                  {showDetails && (
                    <td className="text-right py-1 px-1">
                      {m.employment_pct}%
                    </td>
                  )}
                  <td className="text-right py-1 px-1 font-mono">
                    {m.expected_hours.toFixed(1)}
                  </td>
                  <td className="text-right py-1 px-1 font-mono">
                    {m.actual_hours.toFixed(1)}
                  </td>
                  <td
                    className={`text-right py-1 px-1 font-mono ${m.delta >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                  >
                    {formatHours(m.delta)}
                  </td>
                  <td
                    className={`text-right py-1 pl-1 font-mono font-medium ${monthBalances[i] >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                  >
                    {formatHours(monthBalances[i])}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-medium border-t">
                <td className="py-1.5 pr-2">Total</td>
                <td colSpan={showDetails ? 4 : 1}></td>
                <td className="text-right py-1.5 px-1 font-mono">
                  {visibleMonths
                    .reduce((s, m) => s + m.expected_hours, 0)
                    .toFixed(1)}
                </td>
                <td className="text-right py-1.5 px-1 font-mono">
                  {visibleMonths
                    .reduce((s, m) => s + m.actual_hours, 0)
                    .toFixed(1)}
                </td>
                {(() => {
                  const totalDelta = visibleMonths.reduce(
                    (s, m) => s + m.delta,
                    0,
                  );
                  return (
                    <td
                      className={`text-right py-1.5 px-1 font-mono ${totalDelta >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                    >
                      {formatHours(totalDelta)}
                    </td>
                  );
                })()}
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
